import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../../redux/slices/accountSlice";
import ListTabs from "../DropdownsAndTabs/ListTabs";
import { Colors } from "../../utils/colors";
import { getUserFollowersRef } from "../../utils/FirebaseUtils";
import { getCountFromServer, query, where } from "../../firebase";
import {
  GraphTimelineTabs,
  TimelineLabels,
  getGraphTimelineDate,
  isTimestampWithinRange,
  timelineStartDate,
  toFixedNumber,
  MARKITAI,
  handlePluralString,
} from "@markit/common.utils";
import { CircularProgress } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { FollowerStatus } from "@markit/common.types";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { Icon } from "@iconify/react";
import { CopyableLinkDisplay } from "../Links/CopyCustomLinkify";
import PopupModalMessageTag from "../Containers/PopupModalMessageTag";

export const DataGraphContainer = () => {
  const { accountData, appInitialized, followersData } =
    useSelector(getAccountSlice);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(1);
  const [loadingData, setLoadingData] = useState(false);
  const [followersCountData, setFollowersCountData] = useState<
    Map<string, number[]>
  >(new Map<TimelineLabels, number[]>());

  const styles = {
    smallText: { fontSize: 12, color: Colors.GRAY2 },
    timelineBorder: {
      borderLeft: "0.5px solid #929292",
      borderBottom: "0.5px solid #929292",
      borderRight: "0.5px solid #929292",
    },
    tabContainer: {
      borderRadius: 100,
      width: "20%",
      justifyContent: "center",
    },
  };

  const subscribedFollowers = useMemo(
    () =>
      followersData.filter(
        (follower) => follower.status === FollowerStatus.SUBSCRIBED
      ),
    [followersData]
  );

  const selectedTab = useMemo(
    () => GraphTimelineTabs[selectedTabIndex],
    [selectedTabIndex]
  );

  const startDate = useMemo(
    () =>
      timelineStartDate(
        selectedTab,
        accountData.completedCreatorSetupTimestamp
      ),
    [accountData.completedCreatorSetupTimestamp, selectedTab]
  );

  const followersToShow = useMemo(() => {
    if (loadingData) {
      return undefined;
    }
    const numFollowersData = followersCountData.get(selectedTab);
    if (numFollowersData) {
      // Need to format the data with a name and value for the ListChart to take in
      const timelineFollowersData = numFollowersData.map((number, index) => ({
        name: index.toString(),
        value: number,
      }));
      return timelineFollowersData;
    }
    return [];
  }, [followersCountData, loadingData, selectedTab]);

  const newFollowersPerData = useMemo(() => {
    const numFollowersToShow = followersCountData.get(selectedTab);
    if (numFollowersToShow) {
      return numFollowersToShow.reduce((prev, curr) => prev + curr, 0);
    } else {
      return 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followersCountData, selectedTab, followersToShow]);

  const queryFollowersForHour = useCallback(
    async (startDate: Date) => {
      const startOfHour = new Date(startDate);
      startOfHour.setMinutes(0, 0, 0);

      const endOfHour = new Date(startDate);
      endOfHour.setMinutes(59, 59, 999);

      const followersRef = getUserFollowersRef(accountData.uid);
      const query_ = query(
        followersRef,
        where("status", "==", FollowerStatus.SUBSCRIBED),
        where("createdAt", ">=", startOfHour.toISOString()),
        where("createdAt", "<=", endOfHour.toISOString())
      );
      const snapshot = await getCountFromServer(query_);
      return snapshot.data().count;
    },
    [accountData.uid]
  );

  const queryFollowersForDay = useCallback(
    async (startDate: Date) => {
      const startOfDay = startDate.toISOString();
      const endOfDay = new Date(startDate);
      endOfDay.setHours(23, 59, 59, 999);

      const followersRef = getUserFollowersRef(accountData.uid);
      const query_ = query(
        followersRef,
        where("status", "==", FollowerStatus.SUBSCRIBED),
        where("createdAt", ">=", startOfDay),
        where("createdAt", "<", endOfDay.toISOString())
      );
      const snapshot = await getCountFromServer(query_);
      return snapshot.data().count;
    },
    [accountData.uid]
  );

  const queryFollowersForMonth = useCallback(
    async (startDate: Date) => {
      const startOfMonth = new Date(startDate);
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const endOfMonth = new Date(startDate);
      endOfMonth.setMonth(endOfMonth.getMonth() + 1);
      endOfMonth.setDate(0);
      endOfMonth.setHours(23, 59, 59, 999);

      const followersRef = getUserFollowersRef(accountData.uid);
      const query_ = query(
        followersRef,
        where("status", "==", FollowerStatus.SUBSCRIBED),
        where("createdAt", ">=", startOfMonth.toISOString()),
        where("createdAt", "<=", endOfMonth.toISOString())
      );
      const snapshot = await getCountFromServer(query_);
      return snapshot.data().count;
    },
    [accountData.uid]
  );

  const countFollowersInRange = useCallback(async () => {
    const tempFollowersData: number[] = [];
    const currentDate = new Date(startDate);
    if (!followersCountData.has(selectedTab)) {
      while (currentDate <= new Date()) {
        try {
          // to factor out the refactor to new followers data
          const specifiedDate = new Date("2023-10-18");
          const startOfDay = new Date(specifiedDate);
          startOfDay.setHours(0, 0, 0, 0);
          const endOfDay = new Date(specifiedDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (
            !isTimestampWithinRange(
              currentDate.toISOString(),
              startOfDay.toISOString(),
              endOfDay.toISOString()
            )
          ) {
            let count = 0;
            if (selectedTab === TimelineLabels.ONE_DAY) {
              count = await queryFollowersForHour(currentDate);
              tempFollowersData.push(count);
            } else if (
              selectedTab === TimelineLabels.YR_TO_DAY ||
              selectedTab === TimelineLabels.ALL
            ) {
              count = await queryFollowersForMonth(currentDate);
              tempFollowersData.push(count);
            } else {
              count = await queryFollowersForDay(currentDate);
              tempFollowersData.push(count);
            }
          }
        } catch (error) {
          console.error("Error fetching followers:", error);
        }
        if (selectedTab === TimelineLabels.ONE_DAY) {
          currentDate.setHours(currentDate.getHours() + 1);
        } else if (
          selectedTab === TimelineLabels.YR_TO_DAY ||
          selectedTab === TimelineLabels.ALL
        ) {
          currentDate.setMonth(currentDate.getMonth() + 1);
        } else {
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
      followersCountData.set(selectedTab, tempFollowersData);
      setFollowersCountData(followersCountData);
    }
  }, [
    followersCountData,
    queryFollowersForDay,
    queryFollowersForHour,
    queryFollowersForMonth,
    selectedTab,
    startDate,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (subscribedFollowers.length === 0) {
      return;
    }
    setLoadingData(true);
    await countFollowersInRange();
    setLoadingData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabIndex]);

  // Gate on appInitialized too - followersData starts empty in redux, so before it loads
  // subscribedFollowers.length === 0 doesn't yet mean the creator actually has no followers
  const isEmptyState = appInitialized && subscribedFollowers.length === 0;

  return (
    <div
      className="ColumnNormal"
      style={{ paddingInline: 14, gap: 4, height: "100%" }}
    >
      <ListTabs
        tabLabels={GraphTimelineTabs}
        tabNumbers={[-1, -1, -1, -1, -1]}
        selectedValue={selectedTabIndex}
        onChange={(index: number) => setSelectedTabIndex(index)}
        containerStyles={{ justifyContent: "space-between" }}
        tabStyles={{
          ...styles.tabContainer,
          backgroundColor: Colors.WHITE,
          border: "none",
        }}
        selectedTabStyles={{
          ...styles.tabContainer,
          backgroundColor: Colors.WHITE1,
          border: `0.5px solid ${Colors.GRAY2}`,
        }}
        altColor={Colors.WHITE1}
        newTextColor={Colors.GRAY3}
        newSelectedTextColor={Colors.BLACK}
      />
      <div
        className="ColumnNormal"
        style={{
          gap: 7,
          flex: 1,
          minHeight: 0,
          justifyContent: isEmptyState ? "flex-end" : "flex-start",
        }}
      >
        {!appInitialized ? (
          <div className="ColumnNormal">
            <div className="AlignedRow" style={{ height: 30 }}>
              <CircularProgress style={{ color: Colors.GRAY1 }} size={17} />
            </div>
            <div
              className="Centering"
              style={{ ...styles.timelineBorder, height: 64 }}
            >
              <CircularProgress style={{ color: Colors.GRAY1 }} size={18} />
            </div>
          </div>
        ) : subscribedFollowers.length > 0 ? (
          <div className="ColumnNormal">
            <div className="AlignedRow" style={{ height: 30 }}>
              {loadingData ? (
                <CircularProgress style={{ color: Colors.GRAY1 }} size={17} />
              ) : (
                <div>
                  <span style={{ fontSize: 24 }}>{newFollowersPerData}</span>
                  {selectedTab !== TimelineLabels.ALL ? (
                    <span
                      style={{
                        ...styles.smallText,
                        color: Colors.GREEN2,
                        marginLeft: 4,
                      }}
                    >
                      +
                      {toFixedNumber(
                        (newFollowersPerData /
                          Math.max(
                            subscribedFollowers.length - newFollowersPerData,
                            1
                          )) *
                          100,
                        1
                      )}
                      %
                    </span>
                  ) : null}
                </div>
              )}
            </div>
            <div className="AlignedRowSpaced">
              <PopupModalMessageTag
                message=""
                alternateMessageComp={
                  <div className="AlignedRow" style={{ gap: 4 }}>
                    <span className="smallBodySubtext">
                      {handlePluralString("New Follower", newFollowersPerData)}
                    </span>
                    <Icon
                      icon="mdi:information-outline"
                      height={12}
                      style={{ color: Colors.GRAY1, paddingTop: 1 }}
                    />
                  </div>
                }
                popupTitle=""
                popupSubtext={
                  <span
                    className="bodySmallRegular"
                    style={{ color: Colors.BLACK }}
                  >
                    The number of new subscribers who followed you. Shows the
                    percent change from the same duration in the previous time
                    range.
                  </span>
                }
                popupPosition="top"
                onHover
              />
              <span style={styles.smallText}>
                {getGraphTimelineDate(startDate)} - Today
              </span>
            </div>
          </div>
        ) : null}
        {!appInitialized ? null : subscribedFollowers.length > 0 ? (
          loadingData ? (
            <div
              className="Centering"
              style={{ ...styles.timelineBorder, height: 64 }}
            >
              <CircularProgress style={{ color: Colors.GRAY1 }} size={18} />
            </div>
          ) : followersToShow && newFollowersPerData > 0 ? (
            <ResponsiveContainer style={styles.timelineBorder} height={64}>
              <LineChart
                data={followersToShow}
                margin={{ left: 0, right: 0, bottom: 0, top: 4 }}
              >
                <XAxis hide />
                <YAxis hide />
                <Line
                  dot={false}
                  dataKey="value"
                  stroke={Colors.BLUE5}
                  strokeWidth={1.5}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div
              className="Centering"
              style={{ ...styles.timelineBorder, height: 64 }}
            >
              <span style={styles.smallText}>
                No followers found within the time range
              </span>
            </div>
          )
        ) : (
          <div className="ColumnNormal" style={{ gap: 10 }}>
            <span style={styles.smallText}>
              Copy your profile URL - perfect for sharing on your socials
            </span>

            <CopyableLinkDisplay
              copyText={`${MARKITAI}/u/${accountData.username}`}
              containerStyles={{
                display: "flex",
                padding: 14,
                backgroundColor: Colors.WHITE1,
                borderColor: Colors.GRAY1,
                borderWidth: 0.5,
              }}
              altTextColor={Colors.BLACK}
            />
          </div>
        )}
      </div>
    </div>
  );
};
